import React from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { GET_PLAN_CHANGE_HISTORY } from '../../graphql/billingQueries';
import { formatDate, formatMoney } from '../../utils/format';
import EmptyState from '../ui/EmptyState';
import LoadingState from '../ui/LoadingState';
import StatusChip from '../ui/StatusChip';
import { billingCurrency, formatPlanIdForDisplay, parseDate } from './billingModel';
import type { PlanChangeEntry } from './billingTypes';

/** Past upgrades, downgrades and switches, newest first as the API returns them. */
const PlanHistoryDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { data, loading, error, refetch } = useQuery(GET_PLAN_CHANGE_HISTORY, {
    skip: !open,
    onError: (queryError) => console.error('Plan change history error:', queryError),
  });
  const changes: PlanChangeEntry[] = data?.getPlanChangeHistory?.changes || [];

  let body: React.ReactNode;
  if (loading && !data) {
    body = <LoadingState variant="list" count={3} label="Loading plan history" />;
  } else if (error) {
    body = (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void refetch()}>
            Retry
          </Button>
        }
      >
        We couldn&apos;t load your plan history.
      </Alert>
    );
  } else if (changes.length === 0) {
    body = (
      <EmptyState
        compact
        icon={<HistoryOutlinedIcon />}
        title="No plan changes yet"
        description="Upgrades, downgrades and cancellations appear here."
      />
    );
  } else {
    body = (
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {changes.map((change) => {
          const date = parseDate(change.timestamp);
          const charge = Number(change.prorationDetails?.additionalCharge);
          return (
            <Box
              component="li"
              key={change.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 1.5,
                borderTop: 1,
                borderColor: 'divider',
                '&:first-of-type': { borderTop: 0 },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatPlanIdForDisplay(change.oldPlanId)} to {formatPlanIdForDisplay(change.newPlanId)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {[
                    date ? formatDate(date) : null,
                    charge > 0 ? `${formatMoney(charge, billingCurrency(change.prorationDetails?.currency))} charged` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </Typography>
              </Box>
              <StatusChip
                kind="billing"
                status={change.success ? 'COMPLETED' : 'FAILED'}
                label={change.success ? 'Completed' : 'Failed'}
                tone={change.success ? 'done' : 'problem'}
              />
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Plan history</DialogTitle>
      <DialogContent>{body}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanHistoryDialog;
